import { Link, NavLink } from "react-router-dom";
import { Instagram, Facebook, Mail, Menu, X, ShoppingBag, CreditCard, Globe } from "lucide-react";
import { useState, useEffect, useMemo } from "react";
import { useCart } from "@/context/CartContext";
import { useCurrency } from "@/context/CurrencyContext";
import { usePageTracking } from "@/hooks/usePageTracking";

const NAV = [
  { to: "/", label: "Home", end: true },
  { to: "/gallery", label: "Collection" },
  { to: "/business", label: "For Businesses" },
  { to: "/custom", label: "Commission" },
  { to: "/contact", label: "Contact" },
];

export const PublicNav = ({ transparent = false }) => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const utm = Object.fromEntries([...q.entries()].filter(([k]) => k.startsWith("utm_")));
    if (Object.keys(utm).length) localStorage.setItem("fabj_utm", JSON.stringify({ ...utm, at: Date.now() }));
  }, []);
  const { count } = useCart();
  const cur = useCurrency();
  const currencyCodes = useMemo(() => Object.keys(cur?.available || {}).sort(), [cur?.available]);

  useEffect(() => {
    if (!transparent) return;
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, [transparent]);

  const isDark = transparent && !scrolled;
  const headerCls = transparent
    ? `fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? "bg-cream/95 backdrop-blur-md border-b border-ink-10" : ""}`
    : "sticky top-0 z-50 bg-cream/95 backdrop-blur-md border-b border-ink-10";
  const textCls = isDark ? "text-[color:var(--brand-cream)]" : "text-ink";
  const linkBase = isDark
    ? "text-[color:var(--brand-cream)] hover:text-accent"
    : "text-ink hover:text-accent";
  return (
    <header className={headerCls}>
      {transparent && !scrolled && (
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-black/40 to-transparent pointer-events-none"/>
      )}
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className={`font-serif text-2xl md:text-[26px] tracking-tight ${textCls}`}>
          Fine Art by Jason
        </Link>
        <nav className="hidden md:flex items-center gap-10">
          {NAV.map(l => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-[11px] font-medium tracking-[0.22em] uppercase transition-colors ${isActive ? "text-accent" : linkBase}`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Link to="/cart" data-testid="nav-cart" className={`relative transition-colors ${linkBase}`}>
            <ShoppingBag size={18}/>
            {count > 0 && (
              <span data-testid="nav-cart-count" className="absolute -top-2 -right-2 bg-accent text-[10px] rounded-full w-4 h-4 flex items-center justify-center text-[color:var(--brand-cream)]">
                {count}
              </span>
            )}
          </Link>
          {cur?.ready && (
            <div className={`relative flex items-center gap-1 ${linkBase}`} data-testid="currency-selector-wrapper">
              <Globe size={14}/>
              <select
                value={cur.currency}
                onChange={(e) => cur.change(e.target.value)}
                data-testid="currency-selector"
                className="bg-transparent border-none outline-none text-[11px] font-medium tracking-[0.12em] cursor-pointer appearance-none pr-1"
                style={{ color: "inherit" }}
              >
                {currencyCodes.map(c => (
                  <option key={c} value={c} style={{ color: "#111", background: "#EFE9DD" }}>{c}</option>
                ))}
              </select>
            </div>
          )}
        </nav>
        <div className={`md:hidden flex items-center gap-4 ${textCls}`}>
          <Link to="/cart" className="relative"><ShoppingBag size={20}/>{count > 0 && <span className="absolute -top-2 -right-2 bg-accent text-[10px] rounded-full w-4 h-4 flex items-center justify-center text-[color:var(--brand-cream)]">{count}</span>}</Link>
          <button onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>
      {open && (
        <div className="md:hidden bg-cream border-t border-ink-10">
          <div className="flex flex-col px-6 py-4 gap-4">
            {NAV.map(l => (
              <NavLink key={l.to} to={l.to} end={l.end} onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                className={({ isActive }) => `text-xs tracking-[0.22em] uppercase ${isActive ? "text-accent" : "text-ink"}`}>
                {l.label}
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </header>
  );
};

export const Footer = () => (
  <footer className="bg-cream border-t border-ink-10 mt-24">
    <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-16 grid md:grid-cols-3 gap-12">
      <div>
        <div className="font-serif text-2xl mb-4 text-ink">Fine Art by Jason</div>
        <p className="text-sm text-ink-soft leading-relaxed max-w-sm">
          Original artwork and limited edition prints. Each piece tells a story, crafted with intention and soul.
        </p>
      </div>
      <div>
        <div className="overline mb-5 text-mute">Navigate</div>
        <ul className="space-y-3 text-sm">
          <li><Link to="/gallery" className="text-ink hover:text-accent">Collection</Link></li>
          <li><Link to="/custom" className="text-ink hover:text-accent">Request Commission</Link></li>
          <li><Link to="/reviews" className="text-ink hover:text-accent">Reviews</Link></li>
          <li><Link to="/contact" className="text-ink hover:text-accent">Contact</Link></li>
        </ul>
      </div>
      <div>
        <div className="overline mb-5 text-mute">Secure Payments</div>
        <div className="flex flex-wrap gap-2 mb-6">
          <span className="pay-pill"><CreditCard size={12}/> Credit Card</span>
          <span className="pay-pill">Venmo</span>
          <span className="pay-pill">PayPal</span>
          <span className="pay-pill">CashApp</span>
        </div>
        <div className="flex gap-4">
          <a href="https://instagram.com" target="_blank" rel="noreferrer" aria-label="Instagram" data-testid="footer-instagram" className="text-ink hover:text-accent"><Instagram size={18} /></a>
          <a href="https://facebook.com" target="_blank" rel="noreferrer" aria-label="Facebook" data-testid="footer-facebook" className="text-ink hover:text-accent"><Facebook size={18} /></a>
          <a href="mailto:jason@fineartbyjason.com" aria-label="Email" data-testid="footer-email" className="text-ink hover:text-accent"><Mail size={18} /></a>
        </div>
      </div>
    </div>
    <div className="border-t border-ink-10">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-6 text-center text-[11px] tracking-[0.2em] uppercase text-mute">
        © {new Date().getFullYear()} Fine Art by Jason — All Rights Reserved
      </div>
    </div>
  </footer>
);

export const PublicShell = ({ children, transparentNav = false }) => {
  usePageTracking();
  return (
    <div className="public-app min-h-screen flex flex-col">
      <PublicNav transparent={transparentNav} />
      {!transparentNav && <div className="h-0" />}
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
};

export default PublicShell;
